/*
 * Copyright (c) 2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useEffect, useRef, useState} from "react";
import BookingLoginReservationButtonInner
    from "pages/Front/Booking/BookingLogin/components/BookingLoginReservationDetail/components/BookingLoginReservationButtonInner";
import {BookingLoginReservationButtonSize} from "types/enums/BookingLoginReservationButtonSize";
import {BookingLoginReservationButtonStyle} from "types/enums/BookingLoginReservationButtonStyle";
import {ArrowUturnLeftIcon} from "@heroicons/react/24/solid";
import BookingLoginReservationButton
    from "pages/Front/Booking/BookingLogin/components/BookingLoginReservationDetail/components/BookingLoginReservationButton";
import {RouteComponentProps, useHistory, withRouter} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {useDispatch} from "useDispatch";
import {Reservation} from "types/entities/Reservation";
import {useTypedSelector} from "helpers/reducers";
import BookingAction from "store/booking/BookingAction";
import {PackageCategory} from "types/enums/PackageCategory";
import {DEFAULT_HALL_ID} from "helpers/config";
import {consoleLog, packageItemUpsellPersonType, sortPackageItemsByPosition} from "utils/utility";
import BookingPackageUpsellsHeader from "pages/Front/Booking/components/PackageUpsell/BookingPackageUpsellsHeader";
import {useClearUpsells} from "utils/packageUtils";
import useCustomTimeslotsNew from "utils/hooks/useCustomTimeslotsNew";
import {PackageItemPersonType} from "types/enums/PackageItemPersonType";
import {PackageItemItemType} from "types/enums/PackageItemItemType";
import {toast} from "react-toastify";
import {ArrowRightIcon} from "@heroicons/react/24/outline";
import {useGetGroupedUpsells} from "utils/upsellsUtils";
import Skeleton from "react-loading-skeleton";
import UpsellGroupNameList from "pages/Front/Booking/BookingUpsells/UpsellGroupNameList";

interface Params {
    reservation_id: string;
    cart_guid: string;
}

interface BookingLoginReservationDetailUpsellsProps extends RouteComponentProps<Params> {}

const BookingLoginReservationDetailUpsells = (props: BookingLoginReservationDetailUpsellsProps) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const history = useHistory();
    const reservation_id = +props.match.params.reservation_id;
    const cart_guid = props.match.params.cart_guid;
    const bookingProcess = useTypedSelector(state => state.bookingState.bookingProcess);
    const bookingFilter = bookingProcess.filter;
    const reservations = useTypedSelector(state => state.bookingLoginState.reservations);
    const reservation = reservations.find(reservation => reservation.id === reservation_id) as Reservation; // this is always a reservation
    const packageItemsWithoutGroupOptions = bookingProcess.availablePackageItems?.upsells;
    const selectedUpsells = bookingProcess.upsell;
    const clearPackagesHook = useClearUpsells(bookingProcess);
    const [persons, setPersons] = useState<number>(0);
    const [initUpsells, setInitUpsells] = useState<boolean>(false);

    const scrollRefTimeslots = useRef<HTMLDivElement>(null);
    const scrollRefCustomTimeslots = useRef<HTMLDivElement>(null);
    const scrollRefConfirmTimeslots = useRef<HTMLDivElement>(null);

    const {bookAdditionalUpsells, isLoading} = useCustomTimeslotsNew(() => {}, props.history, [
        scrollRefTimeslots,
        scrollRefCustomTimeslots,
        scrollRefConfirmTimeslots,
    ]);

    const sortedPackageItemsList = packageItemsWithoutGroupOptions ? sortPackageItemsByPosition(packageItemsWithoutGroupOptions) : [];
    const packageItemsList =
        sortedPackageItemsList.length > 0
            ? sortedPackageItemsList
                  .filter(packageItem => {
                      return packageItemUpsellPersonType(bookingFilter, packageItem.person_type);
                  })
                  .filter(packageItem => {
                      if (packageItem.minimum_persons_count === 0) return true;
                      if (packageItem.minimum_persons_count > 0) {
                          return persons >= packageItem.minimum_persons_count;
                      }
                  })
            : [];

    const presetFilterFromReservation = () => {
        if (!reservation) {
            return;
        }

        const reservationDate = reservation.date;
        const reservationCategory = reservation.category;
        const adultCount = reservation.packagesCart?.drivers_count_adult ?? 0;
        const childCount = reservation.packagesCart?.drivers_count_child ?? 0;
        const reservationPackagesCart = reservation?.packagesCart;
        const reservationPackagesCartItems = reservationPackagesCart?.packagesCartItems;

        // set person count
        setPersons(+adultCount + +childCount);

        const newFilter = {
            ...bookingFilter,
            date: reservationDate,
            category: reservationCategory ? (reservationCategory as PackageCategory) : PackageCategory.General,
            adults: adultCount,
            children: childCount,
            activity_ids: bookingFilter.activity_ids.length === 0 ? [1] : bookingFilter.activity_ids,
        };
        console.log(newFilter);
        // set filter
        dispatch(
            BookingAction.setFilter({
                ...bookingFilter,
                date: reservationDate,
                category: reservationCategory ? (reservationCategory as PackageCategory) : PackageCategory.General,
                adults: adultCount,
                children: childCount,
                activity_ids: reservation?.firstTimeslot?.activity_id ? [reservation.firstTimeslot.activity_id] : [1],
            })
        );

        // set packages items
        if (reservationPackagesCartItems && reservationPackagesCartItems.length > 0) {
            reservationPackagesCartItems
                .filter(item => item.packageItem && item.packageItem.type == PackageItemItemType.PackageItem)
                .map((item, index) => {
                    if (item.packageItem) {
                        dispatch(
                            BookingAction.setSelectedPackageItem({
                                id: item.packageItem.id,
                                personIndex: index,
                                person_type: item.packageItem.person_type || PackageItemPersonType.Adult,
                                quantity: item.drivers_count_adult + item.drivers_count_child,
                            })
                        );
                    }
                });
        }

        // package cart
        if (reservationPackagesCart) {
            dispatch(BookingAction.setPackageCart(reservationPackagesCart));
        }

        // timeslot
        if (!reservation.firstTimeslot) {
            toast.error("contact support");
            return;
        }

        dispatch(BookingAction.setPackageItemsSelectedTimeslot(reservation.firstTimeslot));

        // fetch available packages
        dispatch(
            BookingAction.fetchPackagesByCategoryAndTimeSlot({
                adults: adultCount,
                children: childCount,
                date: reservationDate,
                package_category_name: reservationCategory ? (reservationCategory as PackageCategory) : PackageCategory.General,
                hall_id: DEFAULT_HALL_ID,
                activity_ids: [reservation.firstTimeslot.activity_id],
                timeslot_id: reservation.firstTimeslot.id,
            })
        ).then(r => {
            consoleLog("fetchPackagesByCategoryAndTimeSlot: res", r);

            // allow show upsells
            setInitUpsells(true);
        });
    };

    const submitPackageUpsells = (): void => {
        const packageCart = bookingProcess.packageCart;
        if (!packageCart || !bookingProcess.selectedTimeslot || !selectedUpsells) {
            toast.error("No packageCart found");
            return;
        }

        bookAdditionalUpsells(packageCart);
    };

    const scrollToTop = () => {
        console.log("scrollToTop");
        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    };

    const {sortedByGroupNames} = useGetGroupedUpsells(packageItemsList);

    useEffect(() => {
        clearPackagesHook.clearInvalidUpsells();
    }, [selectedUpsells]);

    useEffect(() => {
        presetFilterFromReservation();
    }, [reservation]);

    useEffect(() => {
        console.log(location.pathname);
        scrollToTop();
    }, [location]);

    return (
        <div className={"BookingLoginReservationDetailUpsells w-full"}>
            <div className={"w-full"} ref={scrollRefTimeslots}>
                {!initUpsells && (
                    <>
                        <BookingPackageUpsellsHeader className={"!pt-0 text-gray-900"} title={t("Please wait for your extras")} />
                        <div className={"flex flex-col min-h-56 space-y-4"}>
                            {[1, 2, 3].map((item, index) => (
                                <div key={index} className="flex items-center justify-between bg-white gap-4 mb-4 rounded-xl shadow-card">
                                    <div className="flex items-center gap-4">
                                        <Skeleton width="16rem" height="6rem" />
                                        <div className="flex flex-col gap-2">
                                            <Skeleton className={"w-24 sm:w-48"} height="1.5rem" />
                                            <Skeleton className={"w-24 sm:w-48"} height="1rem" />
                                        </div>
                                    </div>
                                    <div className="flex flex-col gap-2 w-24 sm:w-32 pr-4">
                                        <Skeleton className="w-24 sm:w-32" height="1.5rem" />
                                        <Skeleton className="w-24 sm:w-32" height="1.5rem" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}
                {initUpsells && (
                    <>
                        {packageItemsList.length > 0 && (
                            <>
                                <BookingPackageUpsellsHeader className={"!pt-0 text-gray-900"} title={t("Please select your extras")} />
                                <div className={"space-y-4 transform-gpu duration-500"}>
                                    <UpsellGroupNameList groupNameItems={sortedByGroupNames} />
                                </div>
                            </>
                        )}
                        {packageItemsList.length === 0 && (
                            <BookingPackageUpsellsHeader className={"!pt-0 text-gray-900"} title={t("No upsells found for selected package.")} />
                        )}
                    </>
                )}
            </div>

            <div className={"mt-6 w-full flex gap-4 flex-row justify-between items-center"}>
                <BookingLoginReservationButton type={"button"} onClick={() => history.push("/booking/login/reservations/detail/" + reservation_id)}>
                    <BookingLoginReservationButtonInner size={BookingLoginReservationButtonSize.Normal} style={BookingLoginReservationButtonStyle.Gray}>
                        <ArrowUturnLeftIcon className={"flex-none w-[16px] h-[16px]"} />
                        <span className={"ml-2"}>{t("Back")}</span>
                    </BookingLoginReservationButtonInner>
                </BookingLoginReservationButton>
                <BookingLoginReservationButton type={"button"} onClick={submitPackageUpsells} disabled={selectedUpsells.length === 0}>
                    <BookingLoginReservationButtonInner
                        size={BookingLoginReservationButtonSize.Normal}
                        style={selectedUpsells.length === 0 ? BookingLoginReservationButtonStyle.DisabledBlack : BookingLoginReservationButtonStyle.Green}
                    >
                        <ArrowRightIcon className={"flex-none w-[16px] h-[16px]"} />
                        <span className={"ml-2"}>{t("Next")}</span>
                    </BookingLoginReservationButtonInner>
                </BookingLoginReservationButton>
            </div>
        </div>
    );
};

export default withRouter(BookingLoginReservationDetailUpsells);
